"use client";

import React from "react";
import { Link as ScrollLink } from "react-scroll";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import Icon from "./ui/Icon";

const SECTIONS = [
  { key: "management", to: "management" },
  { key: "marketplace", to: "marketplace" },
  { key: "fintech", to: "fintech" },
  { key: "pricing", to: "pricing" },
  { key: "about", to: "about" },
];

const LEGAL = [
  { key: "privacyPolicy", href: "https://backend-management.cosebu.com/privacy-policy" },
  { key: "termsOfService", href: "https://backend-management.cosebu.com/terms-of-service" },
];

const Footer = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const reduced = useReducedMotion();
  const isHome = location.pathname === "/";

  const linkClass =
    "inline-block py-1 text-[14px] text-ink-2 transition-colors duration-150 hover:text-ink";

  const renderNavLink = (item) =>
    isHome ? (
      <ScrollLink
        to={item.to}
        smooth
        offset={-72}
        duration={reduced ? 0 : 420}
        className={`${linkClass} cursor-pointer`}
      >
        {t.nav[item.key]}
      </ScrollLink>
    ) : (
      <RouterLink to={`/#${item.to}`} className={linkClass}>
        {t.nav[item.key]}
      </RouterLink>
    );

  return (
    <footer className="border-t border-line bg-surface-2">
      <div className="shell py-14 md:py-16">
        <div className="grid gap-10 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <img
                src="/cosebu-logo.png"
                alt=""
                width={26}
                height={26}
                className="h-[26px] w-[26px] object-contain"
              />
              <span className="text-[16px] font-semibold tracking-[-0.02em]">COSEBU</span>
            </div>
            <p className="meta mt-4 max-w-[34ch]">{t.footer.tagline}</p>

            <a
              href={`mailto:${t.footer.email}`}
              className="link mt-6 text-[14px]"
            >
              {t.footer.email}
            </a>
            <p className="meta mt-2 text-[13px]">{t.footer.address}</p>
          </div>

          <nav className="md:col-span-3" aria-label={t.footer.navigation}>
            <h2 className="eyebrow">{t.footer.navigation}</h2>
            <ul className="mt-4 space-y-0.5">
              {SECTIONS.slice(0, 3).map((item) => (
                <li key={item.key}>{renderNavLink(item)}</li>
              ))}
              <li>
                <RouterLink to="/downloads" className={linkClass}>
                  {t.nav.downloads}
                </RouterLink>
              </li>
              {SECTIONS.slice(3).map((item) => (
                <li key={item.key}>{renderNavLink(item)}</li>
              ))}
            </ul>
          </nav>

          <div className="md:col-span-4">
            <h2 className="eyebrow">{t.footer.legal}</h2>
            <ul className="mt-4 space-y-0.5">
              {LEGAL.map((item) => (
                <li key={item.key}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    {t.footer[item.key]}
                  </a>
                </li>
              ))}
            </ul>

            <h2 className="eyebrow mt-8">{t.footer.fromHyphenTech}</h2>
            <a
              href="https://hyphentech.africa"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-[15px] font-medium transition-colors duration-150 hover:text-accent"
            >
              Hyphen Tech
              <Icon name="arrowUpRight" size={14} />
            </a>
          </div>
        </div>

        <div className="mt-12 flex flex-col-reverse items-start justify-between gap-4 border-t border-line pt-6 sm:flex-row sm:items-center">
          <p className="meta text-[13px]">{t.footer.copyright}</p>
          <button
            type="button"
            onClick={() =>
              window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" })
            }
            className="btn btn--ghost btn--sm -mr-3"
          >
            {t.ui.backToTop}
            <Icon name="arrowUp" size={14} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
